import { useUserStore } from "../Stores/User";
import { useAppState } from "../Stores/AppState";
import router from "../router";
import axios from "axios";
import {
    endEntityLocatorNavTrace,
    getActiveEntityLocatorNavTraceId,
    logEntityLocatorNav,
    startEntityLocatorNavTrace,
} from "./EntityLocatorNavDebug";
import {
    armLocatorRouterPush,
    getWorkEntityPathFromUniversalId,
    isAlreadyOnWorkEntityPath,
} from "./workEntityNavigation";
import { EntityResolver } from "../Services/EntityResolver";

const ENTITY_LOCATOR_INTENT_HOLD_MS = 550;

function clearLocatorIntentSoon(appState, logContext = {}) {
    setTimeout(() => {
        logEntityLocatorNav("routeHelper.entity_navigate_with_locator.cleared", {
            holdMs: ENTITY_LOCATOR_INTENT_HOLD_MS,
            ...logContext,
        });
        appState.entity_navigate_with_locator = false;
    }, ENTITY_LOCATOR_INTENT_HOLD_MS);
}

export class RouteHelper {
    // Flag to indicate entity is already active and we're just syncing URL
    // When true, guard will skip EntityNavigator to prevent LocationAnimation
    static skipEntityNavigation = false;
    /**
     * Get entity URL from entity object with sequence_number
     * @param {Object} entity - Entity object with sequence_number
     * @returns {string|null} - URL in format /work/HANDLE-SEQUENCE or null
     */
    static getEntityUrl(entity) {
        if (!entity?.sequence_number) {
            console.warn("RouteHelper: Entity missing sequence_number", entity);
            return null;
        }

        const userStore = useUserStore();
        const handle = userStore.profile?.organization?.handle;

        if (!handle) {
            console.warn("RouteHelper: Unable to get organization handle");
            return null;
        }

        return `/work/${handle}-${entity.sequence_number}`;
    }

    /**
     * Navigate to entity with full LocationAnimation (for breadcrumbs, address bar, shared links)
     * This will trigger the route guard which handles entity loading and animation
     * @param {Object} entity - Entity object with sequence_number
     * @returns {false|Promise<void>} - false when URL construction fails; otherwise a navigation promise
     */
    static navigateToEntity(entity) {
        const url = this.getEntityUrl(entity);

        if (!url) {
            console.warn("RouteHelper: Unable to construct entity URL", entity);
            return false;
        }

        const appState = useAppState();
        // Locator navigations must run EntityNavigator even if a prior URL-sync
        // left skipEntityNavigation sticky. Arm intent before the already-on-URL
        // branch; arm again immediately before router.push below.
        armLocatorRouterPush(this, appState);

        // Already on this URL — run locate animation without router.push
        if (router.currentRoute.value.path === url) {
            // No route guard runs for this branch, so we're responsible for
            // clearing the flag ourselves — safe to do on a short delay since
            // the animation-relevant work above is already fully awaited.
            return appState
                .handleEntityLinkAlreadyActive(entity)
                .finally(() => {
                    clearLocatorIntentSoon(appState, {
                        reason: "already_on_url",
                    });
                });
        }

        // The work-entity route guard clears entity_navigate_with_locator once
        // navigation finishes (see workEntityGuard's finally). On push failure
        // or aborted navigation the guard may never run — clear as a fallback.
        armLocatorRouterPush(this, appState);
        return router.push(url).catch((error) => {
            clearLocatorIntentSoon(appState, {
                reason: "router_push_failed",
            });
            throw error;
        });
    }

    /**
     * Update URL ONLY for already-active entity (ONLY for work panel clicks)
     * Called from click handlers in WorkPanel components (Space/Board/Item.vue)
     * Sets flag to prevent LocationAnimation when entity is already active
     * @param {Object} entity - Entity object with sequence_number
     */
    static updateUrlForEntity(entity) {
        const url = this.getEntityUrl(entity);

        if (!url) {
            console.warn("RouteHelper: Unable to construct entity URL", entity);
            return;
        }

        // Check if we're already on this URL to prevent unnecessary router.push
        if (router.currentRoute.value.path === url) {
            return;
        }

        // Set flag before navigation - indicates entity is already active
        // Guard will check this and skip EntityNavigator/LocationAnimation
        this.stopEntityNavigation();

        // Update URL - guard will see flag and skip navigation logic
        // Note: Flag will be reset by the guard after it checks it
        router.push(url);
    }

    static stopEntityNavigation() {
        this.skipEntityNavigation = true;
    }

    /**
     * Get entity URL from entity ID by calling resolve API
     * @param {string} entityType - Entity type (Item, Board, Workspace, etc.)
     * @param {number} entityId - Entity ID
     * @returns {Promise<string|null>} - URL in format /work/HANDLE-SEQUENCE or null
     */
    static async getEntityUrlFromId(entityType, entityId) {
        try {
            // Backend stores `SequenceNumber.model_type` as title-cased entity names
            // (e.g., "Workspace", "Board", "Item"). Many UI call sites pass
            // lowercase ("workspace"/"board"/"item"), so normalize here.
            const normalizedType =
                {
                    workspace: "Workspace",
                    board: "Board",
                    item: "Item",
                    "item-group": "ItemCategory",
                    "board-group": "BoardCategory",
                    "space-group": "WorkspaceCategory",
                }?.[String(entityType ?? "").toLowerCase()] ?? entityType;

            const response = await axios.get(
                `/entity-id-to-universal/${entityId}`,
                {
                    params: { type: normalizedType },
                },
            );

            if (response.data.success) {
                return {
                    url: `/work/${response.data.data.universal_id}`,
                    universal_id: response.data.data.universal_id,
                };
            }

            console.error("Failed to get universal ID:", response.data.message);
            return null;
        } catch (error) {
            console.error("Error getting entity URL from ID:", error);
            return null;
        }
    }

    /**
     * Navigate to a `/work/HANDLE-SEQUENCE` universal id with locate intent.
     * Used by conversation task/item links so already-on-URL clicks still run
     * EntityNavigator (and clear destination Work Filters) instead of a bare
     * router.push that the work-entity guard would skip.
     *
     * @param {string} universalId
     * @returns {Promise<boolean>}
     */
    static async navigateToWorkUniversalId(universalId) {
        if (!universalId || !EntityResolver.isValidFormat(universalId)) {
            console.error(
                "RouteHelper.navigateToWorkUniversalId: invalid universal id",
                { universalId },
            );
            return false;
        }

        const url = getWorkEntityPathFromUniversalId(universalId);
        const appState = useAppState();

        if (!getActiveEntityLocatorNavTraceId()) {
            startEntityLocatorNavTrace("routeHelper.navigateToWorkUniversalId", {
                universalId,
            });
        }

        armLocatorRouterPush(this, appState);
        logEntityLocatorNav("routeHelper.navigateToWorkUniversalId.start", {
            universalId,
            url,
            currentPath: router.currentRoute.value.path,
        });

        try {
            if (
                isAlreadyOnWorkEntityPath(
                    router.currentRoute.value.path,
                    universalId,
                )
            ) {
                logEntityLocatorNav("routeHelper.branch", {
                    branch: "already_on_url",
                    url,
                    source: "navigateToWorkUniversalId",
                });
                await appState.handleEntityLinkAlreadyActiveByUniversalId(
                    universalId,
                );
                clearLocatorIntentSoon(appState, {
                    reason: "already_on_url",
                    source: "navigateToWorkUniversalId",
                });
                return true;
            }

            logEntityLocatorNav("routeHelper.branch", {
                branch: "router_push",
                url,
                source: "navigateToWorkUniversalId",
            });
            armLocatorRouterPush(this, appState);
            await router.push(url);
            return true;
        } catch (error) {
            endEntityLocatorNavTrace({
                status: "error",
                reason: "navigateToWorkUniversalId_exception",
                message: error?.message,
            });
            clearLocatorIntentSoon(appState, {
                reason: "navigateToWorkUniversalId_exception",
            });
            throw error;
        }
    }

    /**
     * Navigate to entity by ID with full LocationAnimation (for breadcrumbs, activity panel, etc.)
     * Calls API to get universal ID first, then navigates via router
     * Route guard will handle entity loading and animation
     * @param {string} entityType - Entity type
     * @param {number} entityId - Entity ID
     * @returns {Promise<boolean>} - true when route locate/navigation ran; false on early exit
     */
    static async navigateToEntityById(entityType, entityId) {
        if (!entityId) {
            console.error(
                "RouteHelper.navigateToEntityById: missing entity id",
                {
                    entityType,
                    entityId,
                },
            );
            endEntityLocatorNavTrace({
                status: "error",
                reason: "missing_entity_id",
                entityType,
            });
            return false;
        }

        if (!getActiveEntityLocatorNavTraceId()) {
            startEntityLocatorNavTrace("routeHelper.navigateToEntityById", {
                entityType,
                entityId,
            });
        }

        const appState = useAppState();
        // Locator navigations must run EntityNavigator even if a prior URL-sync
        // left skipEntityNavigation sticky (e.g. LeftDrawer Work / panel click).
        // Arm again immediately before router.push after the await below —
        // arming only here leaves a gap for stopEntityNavigation /
        // updateUrlForEntity to re-set skip and make the guard skip locate.
        armLocatorRouterPush(this, appState);
        logEntityLocatorNav("routeHelper.navigateToEntityById.start", {
            entityType,
            entityId,
        });

        try {
            const data = await this.getEntityUrlFromId(entityType, entityId);

            logEntityLocatorNav("routeHelper.resolveEntityUrl", {
                entityType,
                entityId,
                resolvedUrl: data?.url ?? null,
                universalId: data?.universal_id ?? null,
                currentPath: router.currentRoute.value.path,
                samePath: router.currentRoute.value.path === data?.url,
            });

            if (router.currentRoute.value.path === data?.url) {
                logEntityLocatorNav("routeHelper.branch", {
                    branch: "already_on_url",
                    url: data?.url,
                });
                await useAppState().handleEntityLinkAlreadyActiveByUniversalId(
                    data.universal_id,
                );
                clearLocatorIntentSoon(appState, {
                    reason: "already_on_url",
                });
                return true;
            }

            if (data?.url) {
                logEntityLocatorNav("routeHelper.branch", {
                    branch: "router_push",
                    url: data.url,
                });
                armLocatorRouterPush(this, appState);
                await router.push(data.url);
                // Guard owns clearing on success once EntityNavigator finishes.
                return true;
            }

            console.error("Unable to navigate to entity by ID");
            endEntityLocatorNavTrace({
                status: "error",
                reason: "unable_to_resolve_entity_url",
                entityType,
                entityId,
            });
            clearLocatorIntentSoon(appState, {
                reason: "unable_to_resolve_entity_url",
            });
            return false;
        } catch (error) {
            endEntityLocatorNavTrace({
                status: "error",
                reason: "navigateToEntityById_exception",
                message: error?.message,
            });
            clearLocatorIntentSoon(appState, {
                reason: "navigateToEntityById_exception",
            });
            throw error;
        }
    }
}
